import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import ForceGraph2D from 'react-force-graph-2d';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import {
  ArrowLeft,
  Download,
  Mail,
  Edit2,
  Save,
  X,
  FileSearch,
  Calendar,
  Users,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Archive,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  Network,
  MessageSquare,
  FileText,
  Eye,
  Maximize2,
  AlertCircle,
  ArrowUpRight,
  ArrowDownLeft,
  Link2,
  Briefcase,
  GitCompare,
  Play,
  Info,
  XCircle,
  ToggleLeft,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { InvestigacionDialogs } from '../components/investigacion/InvestigacionDialogs';
import { InvestigacionIndividualTab } from '../components/investigacion/InvestigacionIndividualTab';
import { InvestigacionRelacionesTab } from '../components/investigacion/InvestigacionRelacionesTab';
import { InvestigacionRedGlobalTab } from '../components/investigacion/InvestigacionRedGlobalTab';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const COLORS = ['#7c3aed', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const ESTADO_CONFIG = {
  en_proceso: { label: 'En Proceso', color: 'bg-blue-100 text-blue-700', icon: Clock },
  completada: { label: 'Completada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  archivada: { label: 'Archivada', color: 'bg-slate-100 text-slate-700', icon: Archive }
};

const PATTERN_COLORS = {
  STRUCTURING: '#ef4444',
  CONCENTRATOR: '#f97316',
  DISPERSER: '#eab308',
  HIGH_VELOCITY: '#ec4899',
  CIRCULAR: '#dc2626',
  SMURFING: '#8b5cf6',
  LAYERING: '#6366f1',
  DORMANT_ACTIVATION: '#14b8a6',
  ROUND_AMOUNTS: '#f59e0b',
  OFF_HOURS: '#64748b'
};

const InvestigacionDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState('interno');
  const [savingComment, setSavingComment] = useState(false);
  const [graphFullscreen, setGraphFullscreen] = useState(false);
  const [rerunDialog, setRerunDialog] = useState({ open: false, fechaDesde: '', fechaHasta: '' });
  const [rerunning, setRerunning] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [showClientNames, setShowClientNames] = useState(false); // Toggle CVU/Nombre
  const [showNoDataClients, setShowNoDataClients] = useState(false); // Mostrar/ocultar clientes sin txs
  const [showDuplicates, setShowDuplicates] = useState(true); // Mostrar/ocultar duplicados
  const [showWithDataClients, setShowWithDataClients] = useState(true); // Mostrar/ocultar clientes con txs
  const [highlightedEntity, setHighlightedEntity] = useState(null); // Entidad resaltada en el grafo

  useEffect(() => {
    loadInvestigation();
  }, [id]);

  const loadInvestigation = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/investigations/${id}`);
      setInvestigation(res.data);
      setNewTitle(res.data.titulo);
      
      // Seleccionar primer cliente por defecto
      if (res.data.resultados?.analisis_individual?.length > 0) {
        setSelectedClient(res.data.resultados.analisis_individual[0].cliente_id);
      }
    } catch (error) {
      toast.error('Error al cargar la investigación');
      navigate('/m/monitoreo-transaccional/investigaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTitle = async () => {
    try {
      await api.put(`/investigations/${id}`, { titulo: newTitle });
      setInvestigation({ ...investigation, titulo: newTitle });
      setEditingTitle(false);
      toast.success('Título actualizado');
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSavingComment(true);
    try {
      const res = await api.post(`/investigations/${id}/comments`, {
        comentario: newComment,
        tipo: commentType
      });
      setInvestigation(res.data);
      setNewComment('');
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al guardar comentario');
    } finally {
      setSavingComment(false);
    }
  };

  const handleChangeStatus = async (newStatus) => {
    try {
      await api.put(`/investigations/${id}`, { estado: newStatus });
      setInvestigation({ ...investigation, estado: newStatus });
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // Estado para el diálogo de PDF
  const [pdfDialog, setPdfDialog] = useState({ 
    open: false, 
    mode: 'completo', // 'completo', 'seleccion'
    selectedClients: [] 
  });
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const handleOpenPdfDialog = () => {
    // Inicializar con todos los clientes seleccionados
    const allClients = resultados?.analisis_individual?.map(c => c.cliente_id) || [];
    setPdfDialog({ 
      open: true, 
      mode: 'completo',
      selectedClients: allClients 
    });
  };

  const handleTogglePdfClient = (clientId) => {
    setPdfDialog(prev => ({
      ...prev,
      selectedClients: prev.selectedClients.includes(clientId)
        ? prev.selectedClients.filter(id => id !== clientId)
        : [...prev.selectedClients, clientId]
    }));
  };

  const handleSelectAllPdfClients = () => {
    const allClients = resultados?.analisis_individual?.map(c => c.cliente_id) || [];
    setPdfDialog(prev => ({ ...prev, selectedClients: allClients }));
  };

  const handleDeselectAllPdfClients = () => {
    setPdfDialog(prev => ({ ...prev, selectedClients: [] }));
  };

  const handleExportPDF = async () => {
    if (pdfDialog.mode === 'seleccion' && pdfDialog.selectedClients.length === 0) {
      toast.error('Seleccione al menos un cliente');
      return;
    }

    setGeneratingPdf(true);
    toast.info('Generando PDF...');
    try {
      const params = pdfDialog.mode === 'seleccion' 
        ? { clientes: pdfDialog.selectedClients.join(',') }
        : {};
      
      const response = await api.get(`/investigations/${id}/pdf`, {
        responseType: 'blob',
        params
      });
      
      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = pdfDialog.mode === 'seleccion' ? `_${pdfDialog.selectedClients.length}clientes` : '';
      link.setAttribute('download', `reporte_INV-${String(investigation.numero_investigacion).padStart(4, '0')}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
      setPdfDialog({ open: false, mode: 'completo', selectedClients: [] });
    } catch (error) {
      toast.error('Error al generar PDF');
      console.error(error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const [emailDialog, setEmailDialog] = useState({ open: false, recipients: '', message: '', includePdf: true });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const handleSendEmail = async () => {
    if (!emailDialog.recipients.trim()) {
      toast.error('Ingrese al menos un destinatario');
      return;
    }
    
    const recipients = emailDialog.recipients.split(',').map(e => e.trim()).filter(e => e);
    if (recipients.length === 0) {
      toast.error('Ingrese emails válidos separados por coma');
      return;
    }
    
    setSendingEmail(true);
    try {
      await api.post(`/investigations/${id}/send-email`, {
        recipients,
        include_pdf: emailDialog.includePdf,
        custom_message: emailDialog.message || null
      });
      toast.success(`Email enviado a ${recipients.length} destinatario(s)`);
      setEmailDialog({ open: false, recipients: '', message: '', includePdf: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const [generatingAlerts, setGeneratingAlerts] = useState(false);
  const [creatingCase, setCreatingCase] = useState(false);
  const [caseDialog, setCaseDialog] = useState({ open: false, titulo: '', prioridad: 'ALTA', descripcion: '' });
  
  const handleGenerateAlerts = async () => {
    setGeneratingAlerts(true);
    try {
      const res = await api.post(`/investigations/${id}/generate-alerts`);
      if (res.data.alerts_created > 0) {
        toast.success(`Se crearon ${res.data.alerts_created} alerta(s) automáticamente`);
      } else {
        toast.info('No se encontraron hallazgos que requieran alertas');
      }
    } catch (error) {
      toast.error('Error al generar alertas');
    } finally {
      setGeneratingAlerts(false);
    }
  };

  const handleCreateCase = async () => {
    setCreatingCase(true);
    try {
      const res = await api.post(`/investigations/${id}/create-case`, {
        titulo: caseDialog.titulo || null,
        prioridad: caseDialog.prioridad,
        descripcion_adicional: caseDialog.descripcion || null
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setCaseDialog({ open: false, titulo: '', prioridad: 'ALTA', descripcion: '' });
        // Navegar al caso creado
        navigate(`/m/monitoreo-transaccional/caso/${res.data.case.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el caso');
    } finally {
      setCreatingCase(false);
    }
  };

  const handleRerunAnalysis = async () => {
    setRerunning(true);
    try {
      const params = {};
      if (rerunDialog.fechaDesde) params.fecha_desde = rerunDialog.fechaDesde;
      if (rerunDialog.fechaHasta) params.fecha_hasta = rerunDialog.fechaHasta;
      
      const res = await api.post(`/investigations/${id}/rerun`, params);
      setInvestigation(res.data);
      setRerunDialog({ open: false, fechaDesde: '', fechaHasta: '' });
      toast.success('Análisis re-ejecutado exitosamente');
      
      // Seleccionar primer cliente si hay resultados
      if (res.data.resultados?.analisis_individual?.length > 0) {
        setSelectedClient(res.data.resultados.analisis_individual[0].cliente_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al re-ejecutar análisis');
    } finally {
      setRerunning(false);
    }
  };

  // Obtener datos del cliente seleccionado
  const clientData = useMemo(() => {
    if (!investigation?.resultados?.analisis_individual || !selectedClient) return null;
    return investigation.resultados.analisis_individual.find(c => c.cliente_id === selectedClient);
  }, [investigation, selectedClient]);

  // Datos para gráfico de relaciones
  const relationshipGraphData = useMemo(() => {
    if (!investigation?.resultados?.analisis_relaciones?.grafo_relaciones) {
      return { nodes: [], links: [] };
    }
    const grafo = investigation.resultados.analisis_relaciones.grafo_relaciones;
    const relaciones = investigation.resultados.analisis_relaciones;
    
    // Mapear transacciones directas para obtener info de cantidad y volumen
    const txDirectas = relaciones.transacciones_directas || [];
    const txMap = {};
    txDirectas.forEach(tx => {
      const key = `${tx.origen}-${tx.destino}`;
      txMap[key] = tx;
    });
    
    return {
      nodes: grafo.nodes?.map(n => ({
        id: n.id,
        label: n.label,
        color: n.color,
        type: n.type
      })) || [],
      links: grafo.edges?.map(e => {
        const txInfo = txMap[`${e.source}-${e.target}`] || {};
        return {
          source: e.source,
          target: e.target,
          color: e.color,
          type: e.type,
          cantidad: txInfo.cantidad || 0,
          volumen: txInfo.volumen || 0
        };
      }) || []
    };
  }, [investigation]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <RefreshCw size={48} className="animate-spin text-purple-600" />
      </div>
    );
  }

  if (!investigation) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-600">Investigación no encontrada</p>
      </div>
    );
  }

  const estadoConfig = ESTADO_CONFIG[investigation.estado] || ESTADO_CONFIG.en_proceso;
  const EstadoIcon = estadoConfig.icon;
  const resultados = investigation.resultados;
  const relaciones = resultados?.analisis_relaciones;

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/m/monitoreo-transaccional/investigaciones')}
          >
            <ArrowLeft size={16} className="mr-1" />
            Volver
          </Button>
        </div>
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-lg font-mono bg-purple-100 text-purple-700 px-3 py-1 rounded">
                INV-{String(investigation.numero_investigacion).padStart(4, '0')}
              </span>
              <Badge className={estadoConfig.color}>
                <EstadoIcon size={12} className="mr-1" />
                {estadoConfig.label}
              </Badge>
              {investigation.tipo_analisis === 'individual_relacionado' && (
                <Badge className="bg-amber-100 text-amber-700">
                  <Link2 size={12} className="mr-1" />
                  Análisis Relacionado
                </Badge>
              )}
            </div>
            
            {editingTitle ? (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="text-xl font-bold max-w-lg"
                  style={{ backgroundColor: '#fff' }}
                />
                <Button size="sm" onClick={handleSaveTitle}>
                  <Save size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)}>
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <h1 
                className="text-2xl font-bold text-slate-900 cursor-pointer hover:text-purple-600 flex items-center gap-2"
                onClick={() => setEditingTitle(true)}
              >
                {investigation.titulo}
                <Edit2 size={16} className="text-slate-400" />
              </h1>
            )}
            
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {investigation.fecha_desde} a {investigation.fecha_hasta}
              </span>
              <span className="flex items-center gap-1">
                <Users size={14} />
                {investigation.clientes?.length || 0} cliente{investigation.clientes?.length !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={14} />
                Creada: {formatDate(investigation.created_at)}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Select value={investigation.estado} onValueChange={handleChangeStatus}>
              <SelectTrigger className="w-40" style={{ backgroundColor: '#fff' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en_proceso">En Proceso</SelectItem>
                <SelectItem value="completada">Completada</SelectItem>
                <SelectItem value="archivada">Archivada</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={() => setRerunDialog({ 
                open: true, 
                fechaDesde: investigation.fecha_desde || '', 
                fechaHasta: investigation.fecha_hasta || '' 
              })}
              data-testid="rerun-btn"
            >
              <Play size={14} className="mr-1" />
              Re-ejecutar
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate(`/m/monitoreo-transaccional/investigaciones/comparar?id1=${id}`)}
              data-testid="compare-btn"
            >
              <GitCompare size={14} className="mr-1" />
              Comparar
            </Button>
            <Button variant="outline" onClick={handleOpenPdfDialog} data-testid="export-pdf-btn">
              <Download size={14} className="mr-1" />
              PDF
            </Button>
            <Button variant="outline" onClick={() => setEmailDialog({ ...emailDialog, open: true })} data-testid="send-email-btn">
              <Mail size={14} className="mr-1" />
              Email
            </Button>
            <Button 
              variant="outline" 
              onClick={handleGenerateAlerts}
              disabled={generatingAlerts}
              className="text-amber-600 border-amber-300 hover:bg-amber-50"
              data-testid="generate-alerts-btn"
            >
              {generatingAlerts ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <AlertTriangle size={14} className="mr-1" />}
              Alertas
            </Button>
            <Button 
              onClick={() => setCaseDialog({ ...caseDialog, open: true })}
              style={{ backgroundColor: '#7c3aed', color: 'white' }}
              className="text-white"
              data-testid="create-case-btn"
            >
              <Briefcase size={14} className="mr-1" />
              Caso
            </Button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <InvestigacionDialogs
        investigation={investigation} resultados={resultados}
        emailDialog={emailDialog} setEmailDialog={setEmailDialog} handleSendEmail={handleSendEmail} sendingEmail={sendingEmail}
        caseDialog={caseDialog} setCaseDialog={setCaseDialog} handleCreateCase={handleCreateCase} creatingCase={creatingCase}
        rerunDialog={rerunDialog} setRerunDialog={setRerunDialog} handleRerunAnalysis={handleRerunAnalysis} rerunning={rerunning}
        pdfDialog={pdfDialog} setPdfDialog={setPdfDialog} handleExportPDF={handleExportPDF} generatingPdf={generatingPdf}
        handleTogglePdfClient={handleTogglePdfClient} handleSelectAllPdfClients={handleSelectAllPdfClients} handleDeselectAllPdfClients={handleDeselectAllPdfClients}
      />

      {/* Resumen Global */}
      {resultados?.resumen_global && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Clientes Analizados</p>
                <p className="text-xl font-bold text-slate-900">
                  {resultados.resumen_global.total_clientes}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Transacciones</p>
                <p className="text-xl font-bold text-slate-900">
                  {resultados.resumen_global.total_transacciones?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Volumen Total</p>
                <p className="text-xl font-bold text-green-600">
                  {formatMoney(resultados.resumen_global.volumen_total)}
                </p>
              </div>
            </div>
          </Card>
          {relaciones && (
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${relaciones.resumen?.riesgo_relacion >= 50 ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <AlertTriangle size={20} className={relaciones.resumen?.riesgo_relacion >= 50 ? 'text-red-600' : 'text-amber-600'} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Riesgo Relación</p>
                  <p className={`text-xl font-bold ${relaciones.resumen?.riesgo_relacion >= 50 ? 'text-red-600' : 'text-amber-600'}`}>
                    {relaciones.resumen?.riesgo_relacion || 0}%
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Tabs principales */}
      <Tabs defaultValue="individual" className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="individual" className="data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm text-slate-600">
            <Users size={14} className="mr-1" />
            Análisis Individual
          </TabsTrigger>
          {investigation.tipo_analisis === 'individual_relacionado' && (
            <TabsTrigger value="relaciones" className="data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm text-slate-600">
              <Network size={14} className="mr-1" />
              Cruces y Relaciones
            </TabsTrigger>
          )}
          {resultados?.analisis_red && (
            <TabsTrigger value="red-global" className="data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm text-slate-600">
              <GitCompare size={14} className="mr-1" />
              Red Global
            </TabsTrigger>
          )}
          <TabsTrigger value="comentarios" className="data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm text-slate-600">
            <MessageSquare size={14} className="mr-1" />
            Comentarios
          </TabsTrigger>
        </TabsList>


        {/* Tab: Análisis Individual */}
        <TabsContent value="individual" className="space-y-4">
          <InvestigacionIndividualTab
            resultados={resultados}
            clientData={clientData}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            showClientNames={showClientNames}
            setShowClientNames={setShowClientNames}
            showNoDataClients={showNoDataClients}
            setShowNoDataClients={setShowNoDataClients}
            showDuplicates={showDuplicates}
            setShowDuplicates={setShowDuplicates}
            showWithDataClients={showWithDataClients}
            setShowWithDataClients={setShowWithDataClients}
            selectedPattern={selectedPattern}
            setSelectedPattern={setSelectedPattern}
          />
        </TabsContent>

        {/* Tab: Cruces y Relaciones */}
        {investigation.tipo_analisis === 'individual_relacionado' && relaciones && (
          <TabsContent value="relaciones" className="space-y-4">
            <InvestigacionRelacionesTab
              relaciones={relaciones}
              relationshipGraphData={relationshipGraphData}
              graphFullscreen={graphFullscreen}
              setGraphFullscreen={setGraphFullscreen}
            />
          </TabsContent>
        )}

        {/* Tab: Red Global */}
        {resultados?.analisis_red && (
          <TabsContent value="red-global" className="space-y-4">
            <InvestigacionRedGlobalTab
              resultados={resultados}
              highlightedEntity={highlightedEntity}
              setHighlightedEntity={setHighlightedEntity}
            />
          </TabsContent>
        )}


        {/* Tab: Comentarios */}
        <TabsContent value="comentarios" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Comentarios Internos */}
            <Card className="p-4 bg-white">
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <MessageSquare size={16} />
                Comentarios Internos
                <span className="text-xs text-slate-400 font-normal">(no se exportan)</span>
              </h3>
              
              <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                {investigation.comentarios_internos?.length > 0 ? (
                  investigation.comentarios_internos.map((comment, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700">{comment.usuario}</span>
                        <span className="text-xs text-slate-400">{formatDate(comment.fecha)}</span>
                      </div>
                      <p className="text-sm text-slate-600">{comment.texto}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Sin comentarios internos</p>
                )}
              </div>

              <div className="border-t pt-4">
                <Textarea
                  value={commentType === 'interno' ? newComment : ''}
                  onChange={(e) => {
                    setCommentType('interno');
                    setNewComment(e.target.value);
                  }}
                  placeholder="Agregar comentario interno..."
                  className="mb-2"
                  rows={2}
                  style={{ backgroundColor: '#fff' }}
                />
                <Button 
                  size="sm" 
                  onClick={handleAddComment}
                  disabled={savingComment || !newComment.trim() || commentType !== 'interno'}
                >
                  {savingComment ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}
                  Agregar
                </Button>
              </div>
            </Card>

            {/* Comentarios para Informe */}
            <Card className="p-4 bg-white">
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <FileText size={16} />
                Comentarios para Informe
                <span className="text-xs text-amber-600 font-normal">(se incluyen al exportar)</span>
              </h3>
              
              <div className="mb-4">
                {investigation.comentarios_informe ? (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {investigation.comentarios_informe}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">
                    Sin comentarios para el informe
                  </p>
                )}
              </div>

              <div className="border-t pt-4">
                <Textarea
                  value={commentType === 'informe' ? newComment : (investigation.comentarios_informe || '')}
                  onChange={(e) => {
                    setCommentType('informe');
                    setNewComment(e.target.value);
                  }}
                  placeholder="Escribir comentarios que se incluirán en el informe exportado..."
                  className="mb-2"
                  rows={4}
                  style={{ backgroundColor: '#fff' }}
                />
                <Button 
                  size="sm" 
                  onClick={handleAddComment}
                  disabled={savingComment || (!newComment.trim() && commentType === 'informe')}
                >
                  {savingComment ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}
                  Guardar para Informe
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvestigacionDetalle;
